import { ILocation } from "../types/location";

/**
 * Parses coordinates from either comma/space-separated numbers or Google Maps URLs.
 *
 * @param {string} input - User-entered search string or pasted URL.
 * @returns {{ latitude: number; longitude: number } | null} Parsed latitude and longitude, or null.
 */
export function parseCoordinatesOrUrl(input: string): { latitude: number; longitude: number } | null {
  if (!input || typeof input !== "string") return null;
  const trimmed = input.trim();

  // Pattern 1: @lat,lon in Google Maps URL (e.g., .../@-33.85678,151.21529,17z...)
  const atMatch = trimmed.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (atMatch) {
    const lat = parseFloat(atMatch[1]);
    const lon = parseFloat(atMatch[2]);
    if (!isNaN(lat) && !isNaN(lon)) return { latitude: lat, longitude: lon };
  }

  // Pattern 2: q=lat,lon or ll=lat,lon or query=lat,lon
  const qMatch = trimmed.match(/[?&](?:q|ll|query)=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (qMatch) {
    const lat = parseFloat(qMatch[1]);
    const lon = parseFloat(qMatch[2]);
    if (!isNaN(lat) && !isNaN(lon)) return { latitude: lat, longitude: lon };
  }

  // Pattern 3: direct comma or space separated numbers: "-33.8568, 151.2153"
  const directMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)$/);
  if (directMatch) {
    const lat = parseFloat(directMatch[1]);
    const lon = parseFloat(directMatch[2]);
    if (!isNaN(lat) && !isNaN(lon) && lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
      return { latitude: lat, longitude: lon };
    }
  }

  return null;
}

/**
 * Formats a location into a human-readable display string.
 * Prepends "~ " if the location is flagged as approximate.
 * Falls back to coordinates if text is absent.
 *
 * @param {ILocation | string | null | undefined} loc - Location object or string.
 * @returns {string} Formatted location string.
 */
export function formatLocation(loc?: ILocation | string | null): string {
  if (!loc) return "";
  if (typeof loc === "string") return loc;
  const prefix = loc.isApproximate ? "~ " : "";
  if (loc.text && loc.text.trim()) {
    return `${prefix}${loc.text.trim()}`;
  }
  if (typeof loc.latitude === "number" && typeof loc.longitude === "number") {
    return `${prefix}${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)}`;
  }
  return "";
}

/**
 * Normalizes a structured location payload into a canonical ILocation object.
 * Legacy raw string and detached coordinate URL parsing fallbacks have been removed (Migration 002).
 *
 * @param {any} input - Incoming location payload (structured object).
 * @returns {ILocation | undefined} Normalized ILocation or undefined.
 */
export function parseLocation(input: any): ILocation | undefined {
  if (!input || typeof input !== "object") return undefined;
  const lat = typeof input.latitude === "number" && !isNaN(input.latitude) ? input.latitude : undefined;
  const lon = typeof input.longitude === "number" && !isNaN(input.longitude) ? input.longitude : undefined;
  const text = typeof input.text === "string" ? input.text : undefined;
  const isApprox = Boolean(input.isApproximate);
  if (lat === undefined && lon === undefined && !text) return undefined;
  return {
    ...(lat !== undefined ? { latitude: lat } : {}),
    ...(lon !== undefined ? { longitude: lon } : {}),
    ...(text !== undefined ? { text } : {}),
    ...(isApprox ? { isApproximate: true } : {}),
  };
}
